import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, DialogContent, DialogFooter, toast } from '@vwt/ui';
import { isApiError } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { useTranslation } from '../lib/i18n.js';

export interface ConflictField {
  key: string;
  mine: unknown;
  theirs: unknown;
}

/** Short, readable rendering of a field value for side-by-side comparison. */
function display(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.map((v) => (typeof v === 'object' && v && 'text' in v ? String((v as { text: unknown }).text) : display(v))).join(' · ') || '—';
  if (typeof value === 'object') {
    const named = value as { displayName?: unknown; name?: unknown; id?: unknown };
    return String(named.displayName ?? named.name ?? named.id ?? JSON.stringify(value));
  }
  const text = String(value);
  return text.length > 400 ? `${text.slice(0, 400)}…` : text;
}

/**
 * Conflict comparison after a 409 (Section 14): someone else saved first. Shows each field the
 * person changed next to the current saved value, then re-applies their change on top of the latest
 * version or keeps the current one. Nothing is overwritten without this explicit choice.
 */
export function ConflictDialog({ fields, onApplyMine, onKeepCurrent, busy }: { fields: ConflictField[]; onApplyMine: () => void; onKeepCurrent: () => void; busy?: boolean }) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(open) => !open && onKeepCurrent()}>
      <DialogContent title={t('conflict.title')} description={t('conflict.hint')} closeLabel={t('common.close')}>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm" data-testid="conflict-table">
            <thead className="text-left text-xs text-fg-subtle">
              <tr>
                <th className="py-1 pr-3 font-medium">{t('conflict.field')}</th>
                <th className="py-1 pr-3 font-medium">{t('conflict.mine')}</th>
                <th className="py-1 font-medium">{t('conflict.current')}</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => (
                <tr key={f.key} className="border-t border-border align-top">
                  <td className="py-2 pr-3 font-medium">{t(`conflict.fields.${f.key}`, { defaultValue: f.key })}</td>
                  <td className="py-2 pr-3 whitespace-pre-wrap">{display(f.mine)}</td>
                  <td className="py-2 whitespace-pre-wrap text-fg-muted">{display(f.theirs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onKeepCurrent}>
            {t('conflict.keepCurrent')}
          </Button>
          <Button variant="primary" loading={busy} onClick={onApplyMine}>
            {t('conflict.applyMine')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The saved value matching an edited field: assigneeId -> assignee, repositoryIds -> repositories. */
function savedValue(latest: Record<string, unknown>, key: string): unknown {
  for (const candidate of [key, key.replace(/Id$/, ''), key.replace(/yIds$/, 'ies'), key.replace(/Ids$/, 's')]) {
    if (candidate in latest) return latest[candidate];
  }
  return undefined;
}

/**
 * Wraps an optimistic-concurrency save. On VERSION_CONFLICT it loads the latest record and opens the
 * comparison dialog instead of failing silently. Resolves true only when the change was saved.
 */
export function useConflictAwareSave<T extends { version: number }>(current: T, load: () => Promise<T>, save: (body: Record<string, unknown>, version: number) => Promise<unknown>): { patch: (body: Record<string, unknown>) => Promise<boolean>; dialog: ReactNode } {
  const { t } = useTranslation();
  const [conflict, setConflict] = useState<{ body: Record<string, unknown>; latest: T } | null>(null);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();
  const patch = async (body: Record<string, unknown>) => {
    try {
      await save(body, current.version);
      toast.success(t('common.changesSaved'));
      return true;
    } catch (error) {
      if (isApiError(error) && error.code === 'VERSION_CONFLICT') {
        try {
          setConflict({ body, latest: await load() });
        } catch (loadError) {
          toastError(loadError);
        }
      } else {
        toastError(error);
      }
      return false;
    }
  };
  const dialog = conflict ? (
    <ConflictDialog
      fields={Object.keys(conflict.body).map((key) => ({ key, mine: conflict.body[key], theirs: savedValue(conflict.latest as unknown as Record<string, unknown>, key) }))}
      busy={busy}
      onApplyMine={async () => {
        setBusy(true);
        try {
          await save(conflict.body, conflict.latest.version);
          toast.success(t('common.changesSaved'));
          setConflict(null);
        } catch (error) {
          toastError(error);
        } finally {
          setBusy(false);
        }
      }}
      onKeepCurrent={() => {
        setConflict(null);
        void queryClient.invalidateQueries();
      }}
    />
  ) : null;
  return { patch, dialog };
}
